// Global Instructions Rule Applied!
//
// Interest Accruals workbench (docs/INTEREST_ACCRUAL_DESIGN.md section 6.1).
// For one fund, an accrual date and a side it shows the interest accrued to
// that date against what is already booked, and books the difference as one
// RUN:
//   Receivable - one row per rated tranche of the fund (the interest the fund
//     earns on its notes); a run books one Interest Accrued asset transaction
//     per line through the asset booking engine.
//   Payable - one row per fund loan (the interest and charge the fund owes); a
//     run books one Loan Interest (Accrual) fund transaction through the loan
//     engine.
// The figures are the engine's (at__interest_accrued / loan__accrued minus
// the booked position - self-truing); the screen never computes interest.
// What a person may change is the amount booked (Override) and, where the FX
// feed has no rate for the date, the rate. A run is atomic: a refusal of the
// booking engine books nothing and comes back as its own sentence.
//
// Reusable in two modes, as the Fee Booking workbench: standalone (fund
// select shown) and embedded in the Fund form (fundId fixed, select hidden).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Button, Col, Input, InputNumber, Modal, Radio, Row, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { CalculatorOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import DatePicker from '../../../core/components/DatePicker.tsx'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import {
  TABLE_ACCOUNTS,
  TABLE_ASSET_TRANSACTIONS,
  TABLE_FUND_TRANSACTIONS,
  TABLE_SECURITIES,
  TABLE_TRANCHES
} from '../../../state_manager/globals.ts'
import { useNumberDisplay, useNumberFormat } from '../../../core/utils/inputNumberFormat.ts'
import { FormSection } from '../../../core/components/FormSection.tsx'
import DisabledReason from '../../../core/components/DisabledReason.tsx'
import { useCapability } from '../../../core/hooks/useCapability.ts'
import NumberText from '../../../core/components/NumberText.tsx'
import { FundSelect } from '../../../core/components/FundSelect.tsx'
import { getFundNameById } from '../../../core/utils/lookupLabelFromStore.ts'
import ExportRowsButton from '../../shared/table_grid_export/ExportRowsButton.tsx'
import {
  InterestAccrualPayableRow,
  InterestAccrualPosition,
  InterestAccrualReceivableRow,
  InterestJournalPreviewLine,
  InterestRun,
  InterestRunDetail,
  InterestRunPayableLine,
  InterestRunReceivableLine,
  InterestSide,
  PayableScheduleRow,
  ReceivableScheduleRow
} from '../../../state_manager/data_models/interest_accrual_position.ts'
import {
  bookInterestRun,
  createInterestRun,
  deleteInterestRun,
  fetchInterestAccrualPosition,
  fetchInterestRunDetail,
  fetchInterestRuns,
  unbookInterestRun
} from '../services/interestAccrualsService.ts'

const { Text } = Typography

const DATE_FORMAT = 'MM/DD/YYYY'
const ISO_FORMAT = 'YYYY-MM-DD'
const EPSILON = 0.005

const RECORD_LINK_CLASS =
  'cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'

const SIDE_LABEL: Record<InterestSide, string> = { receivable: 'Receivable', payable: 'Payable' }

// the receivable statuses a run books (the rest are shown, never booked)
const RECEIVABLE_BOOKABLE_STATUSES = new Set<string>(['accrued', 'cash_pay', 'matured_accruing'])

// status Tag colours (docs/FRONTEND_DESIGN_PATTERNS.md, colour & status vocabulary)
const RECEIVABLE_STATUS_COLOR: Record<InterestAccrualReceivableRow['prog__status'], string> = {
  accrued: 'blue',
  cash_pay: 'gold',
  matured_accruing: 'gold',
  exited: '#d46b08',
  no_terms: 'red',
  already_accrued: 'default'
}
const PAYABLE_STATUS_COLOR: Record<InterestAccrualPayableRow['prog__status'], string> = {
  accrued: 'blue',
  matured: 'gold',
  already_accrued: 'default'
}

const RUN_STATUS_COLOR: Record<string, string> = { Booked: 'green', Draft: 'blue' }

// a journal preview row with the amounts the run will book
type PreviewLine = InterestJournalPreviewLine & { prog__debit: number; prog__credit: number; preview_key: string }

interface LoanOverride {
  interest: number | null
  charge: number | null
}

const round2 = (v: number) => Math.round(v * 100) / 100
const formatDate = (v: string | null | undefined) => (v ? dayjs(v).format(DATE_FORMAT) : '—')
const formatRate = (v: number | null | undefined) => (v == null ? '—' : `${(v * 100).toFixed(2)}%`)
const isNonZero = (v: number | null | undefined): v is number => v != null && Math.abs(v) >= EPSILON

// The four quarter ends of a year, Q1..Q4.
const quarterEnds = (year: number) =>
  [3, 6, 9, 12].map((month, i) => ({
    value: dayjs(new Date(year, month, 0)).format(ISO_FORMAT),
    label: `Q${i + 1} ${year}`
  }))

// The latest quarter end on or before today: accruals are booked to a period end.
const defaultAsOf = (): string => {
  const today = dayjs().format(ISO_FORMAT)
  const candidates = [...quarterEnds(dayjs().year() - 1), ...quarterEnds(dayjs().year())]
  return candidates.filter((q) => q.value <= today).map((q) => q.value).pop() ?? today
}

interface InterestAccrualsWorkbenchProps {
  fundId?: string | null
}

export default function InterestAccrualsWorkbench({ fundId: fixedFundId = null }: InterestAccrualsWorkbenchProps) {
  const { openFormInTab } = useTabManagerContext()
  const [modalApi, modalContextHolder] = Modal.useModal()
  // User rights (docs/USER_RIGHTS_DESIGN.md 6.2): disabled with the database's sentence
  const createRight = useCapability('asset_transactions.create')
  const bookRight = useCapability('asset_transactions.book')
  const unbookRight = useCapability('asset_transactions.unbook')
  const deleteRight = useCapability('asset_transactions.delete')
  const currencyFmt = useNumberFormat('currency')
  const fxFmt = useNumberFormat('fx_rate')
  const currencyText = useNumberDisplay('currency')

  const embedded = fixedFundId != null
  const [selectedFundId, setSelectedFundId] = useState<string | null>(fixedFundId)
  const [side, setSide] = useState<InterestSide>('receivable')
  const [asOf, setAsOf] = useState<string>(defaultAsOf)
  const [notes, setNotes] = useState<string>('')

  const [position, setPosition] = useState<InterestAccrualPosition | null>(null)
  const [loading, setLoading] = useState(false)
  const [booking, setBooking] = useState(false)
  // receivable: override amount (security ccy) / typed FX rate, by tranche id
  const [overrides, setOverrides] = useState<Record<string, number | null>>({})
  const [fxInputs, setFxInputs] = useState<Record<string, number | null>>({})
  // payable: interest / charge overrides by loan id
  const [loanOverrides, setLoanOverrides] = useState<Record<string, LoanOverride>>({})

  const [runs, setRuns] = useState<InterestRun[]>([])
  const [runsLoading, setRunsLoading] = useState(false)
  const [expandedRunIds, setExpandedRunIds] = useState<string[]>([])
  const [runDetails, setRunDetails] = useState<Record<string, InterestRunDetail>>({})
  const [runActionId, setRunActionId] = useState<string | null>(null)

  // a Compute answered after the parameters moved on is dropped
  const positionRequest = useRef(0)

  const clearPosition = useCallback(() => {
    positionRequest.current += 1
    setPosition(null)
    setOverrides({})
    setFxInputs({})
    setLoanOverrides({})
  }, [])

  useEffect(() => {
    if (fixedFundId != null) {
      setSelectedFundId(fixedFundId)
      clearPosition()
    }
  }, [fixedFundId, clearPosition])

  const loadPosition = useCallback(async () => {
    if (!selectedFundId) return
    const request = ++positionRequest.current
    setLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchInterestAccrualPosition(selectedFundId, asOf, side, token)
      if (request !== positionRequest.current) return
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to compute the interest accrual position')
        return
      }
      setPosition(res.data)
      setOverrides({})
      setFxInputs({})
      setLoanOverrides({})
    } finally {
      if (request === positionRequest.current) setLoading(false)
    }
  }, [selectedFundId, asOf, side])

  const loadRuns = useCallback(async () => {
    if (!selectedFundId) {
      setRuns([])
      return
    }
    setRunsLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchInterestRuns(selectedFundId, null, token)
      if (res.error || !res.data) {
        showErrorToast(res.error || 'Failed to fetch interest runs')
        return
      }
      setRuns(res.data)
    } finally {
      setRunsLoading(false)
    }
  }, [selectedFundId])

  useEffect(() => {
    setExpandedRunIds([])
    setRunDetails({})
    loadRuns()
  }, [loadRuns])

  // ---------------------------------------------------------------------------
  // Receivable lines
  // ---------------------------------------------------------------------------
  const receivableRows: InterestAccrualReceivableRow[] = position?.side === 'receivable' ? position.data.rows : []
  const payableRows: InterestAccrualPayableRow[] = position?.side === 'payable' ? position.data.rows : []

  const rowFx = useCallback(
    (row: InterestAccrualReceivableRow): number | null => row.prog__fx_rate ?? fxInputs[row.tranches__id] ?? null,
    [fxInputs]
  )
  const rowAmount = useCallback(
    (row: InterestAccrualReceivableRow): number | null => overrides[row.tranches__id] ?? row.prog__this_booking ?? null,
    [overrides]
  )
  const rowFunctional = useCallback(
    (row: InterestAccrualReceivableRow): number | null => {
      const overridden = overrides[row.tranches__id] != null
      if (!overridden && row.prog__fx_rate != null && row.prog__this_booking_functional != null) {
        return row.prog__this_booking_functional
      }
      const amount = rowAmount(row)
      const fx = rowFx(row)
      return amount == null || fx == null ? null : round2(amount * fx)
    },
    [overrides, rowAmount, rowFx]
  )
  // the server's prog__bookable, unless a person typed an override or a rate
  const rowBookable = useCallback(
    (row: InterestAccrualReceivableRow): boolean => {
      const touched = overrides[row.tranches__id] != null || fxInputs[row.tranches__id] != null
      if (!touched) return Boolean(row.prog__bookable)
      const fx = rowFx(row)
      return RECEIVABLE_BOOKABLE_STATUSES.has(row.prog__status) && isNonZero(rowAmount(row)) && fx != null && fx > 0
    },
    [overrides, fxInputs, rowAmount, rowFx]
  )

  // ---------------------------------------------------------------------------
  // Payable lines
  // ---------------------------------------------------------------------------
  const loanInterest = useCallback(
    (row: InterestAccrualPayableRow): number =>
      loanOverrides[row.fund_loans__id]?.interest ?? row.prog__this_interest ?? 0,
    [loanOverrides]
  )
  const loanCharge = useCallback(
    (row: InterestAccrualPayableRow): number => loanOverrides[row.fund_loans__id]?.charge ?? row.prog__this_charge ?? 0,
    [loanOverrides]
  )
  const loanOverridden = useCallback(
    (row: InterestAccrualPayableRow): boolean => {
      const o = loanOverrides[row.fund_loans__id]
      return o != null && (o.interest != null || o.charge != null)
    },
    [loanOverrides]
  )
  const loanBookable = useCallback(
    (row: InterestAccrualPayableRow): boolean => {
      if (!loanOverridden(row)) return Boolean(row.prog__bookable)
      return row.prog__status !== 'already_accrued' && isNonZero(loanInterest(row) + loanCharge(row))
    },
    [loanOverridden, loanInterest, loanCharge]
  )

  const totals = useMemo(() => {
    if (position?.side === 'receivable') {
      const bookable = receivableRows.filter(rowBookable)
      return {
        lineCount: receivableRows.length,
        bookableCount: bookable.length,
        functional: round2(bookable.reduce((acc, r) => acc + (rowFunctional(r) ?? 0), 0))
      }
    }
    if (position?.side === 'payable') {
      const bookable = payableRows.filter(loanBookable)
      return {
        lineCount: payableRows.length,
        bookableCount: bookable.length,
        functional: round2(bookable.reduce((acc, r) => acc + loanInterest(r) + loanCharge(r), 0))
      }
    }
    return { lineCount: 0, bookableCount: 0, functional: 0 }
  }, [position, receivableRows, payableRows, rowBookable, rowFunctional, loanBookable, loanInterest, loanCharge])

  const fund = position?.data.fund ?? null
  const functionalCode = fund?.currencies__code ?? ''

  // The journal preview of what the run will book. The server's preview gives
  // the account rows (item, account) over its calculated amounts; the amounts
  // here are the run's own - the override or the calculated figure of the
  // lines that will be posted - so the preview total is the grid footer's.
  const previewLines: PreviewLine[] = useMemo(() => {
    if (!position) return []
    const lines = position.data.journal_preview ?? []
    const sideAmounts = (natural: 'dr' | 'cr', amount: number) => {
      // a negative amount flips Dr / Cr, as at__interest_preview_line does
      const onDebit = (natural === 'dr') === (amount >= 0)
      return { prog__debit: onDebit ? Math.abs(amount) : 0, prog__credit: onDebit ? 0 : Math.abs(amount) }
    }
    if (position.side === 'receivable') {
      // one row per rule of the accrual pattern, Dr rules first; the server
      // put its total on each, flipped when negative - that gives each
      // row's own side back (by order when the server total is nil)
      const serverTotal = position.data.totals?.this_booking_functional ?? 0
      return lines.map((line, i) => {
        const natural: 'dr' | 'cr' = isNonZero(serverTotal)
          ? isNonZero(line.prog__debit) === serverTotal >= 0
            ? 'dr'
            : 'cr'
          : i < lines.length / 2
            ? 'dr'
            : 'cr'
        return { ...line, ...sideAmounts(natural, totals.functional), preview_key: `${i}` }
      })
    }
    // payable: a Dr row then a Cr row per component posting; the component
    // is the one whose calculated total the server put on the pair
    const bookable = position.data.rows.filter(loanBookable)
    const effective = {
      interest: round2(bookable.reduce((acc, r) => acc + loanInterest(r), 0)),
      charge: round2(bookable.reduce((acc, r) => acc + loanCharge(r), 0))
    }
    const serverBookable = position.data.rows.filter((r) => Boolean(r.prog__bookable))
    const calculated = {
      interest: Math.abs(round2(serverBookable.reduce((acc, r) => acc + (r.prog__this_interest ?? 0), 0))),
      charge: Math.abs(round2(serverBookable.reduce((acc, r) => acc + (r.prog__this_charge ?? 0), 0)))
    }
    const used = new Set<keyof LoanOverride>()
    let component: keyof LoanOverride = 'interest'
    return lines.map((line, i) => {
      const natural: 'dr' | 'cr' = i % 2 === 0 ? 'dr' : 'cr'
      if (natural === 'dr') {
        const amount = Math.max(line.prog__debit ?? 0, line.prog__credit ?? 0)
        const preferred: keyof LoanOverride =
          Math.abs(amount - calculated.interest) <= Math.abs(amount - calculated.charge) ? 'interest' : 'charge'
        component = used.has(preferred) ? (preferred === 'interest' ? 'charge' : 'interest') : preferred
        used.add(component)
      }
      return { ...line, ...sideAmounts(natural, effective[component]), preview_key: `${i}` }
    })
  }, [position, totals.functional, loanBookable, loanInterest, loanCharge])

  // ---------------------------------------------------------------------------
  // Book run
  // ---------------------------------------------------------------------------
  const handleBookRun = useCallback(() => {
    if (!selectedFundId || !position || totals.bookableCount === 0) return
    const fundName = position.data.fund.funds__name ?? 'the fund'
    const what =
      position.side === 'receivable'
        ? `${totals.bookableCount} Interest Accrued transaction${totals.bookableCount === 1 ? '' : 's'}`
        : `one Loan Interest (Accrual) transaction over ${totals.bookableCount} loan${totals.bookableCount === 1 ? '' : 's'}`
    modalApi.confirm({
      title: 'Book interest run?',
      content: `${SIDE_LABEL[position.side]} interest of ${fundName} to ${formatDate(asOf)}: ${what}, ${currencyText(
        totals.functional
      )} ${functionalCode}. Each line books through the booking engine; if one is refused, nothing is booked.`,
      okText: 'Book run',
      onOk: async () => {
        setBooking(true)
        try {
          const token = getAccessToken(store.getState().auth)
          const res =
            position.side === 'receivable'
              ? await createInterestRun(
                  {
                    fund_id: selectedFundId,
                    as_of: asOf,
                    side: 'receivable',
                    notes: notes.trim() || null,
                    lines: position.data.rows.filter(rowBookable).map((r) => ({
                      tranche_id: r.tranches__id,
                      amount: round2(rowAmount(r) ?? 0),
                      override: overrides[r.tranches__id] != null,
                      fx_rate: r.prog__fx_rate == null ? fxInputs[r.tranches__id] ?? null : null
                    }))
                  },
                  token
                )
              : await createInterestRun(
                  {
                    fund_id: selectedFundId,
                    as_of: asOf,
                    side: 'payable',
                    notes: notes.trim() || null,
                    // every loan is a line of the fund transaction; the
                    // entries carry the overrides
                    lines: position.data.rows.map((r) => ({
                      fund_loan_id: r.fund_loans__id,
                      interest_override: loanOverrides[r.fund_loans__id]?.interest ?? null,
                      charge_override: loanOverrides[r.fund_loans__id]?.charge ?? null
                    }))
                  },
                  token
                )
          if (res.error || !res.data) {
            showErrorToast(res.error || 'Failed to book the interest run')
            return
          }
          toast.success(`Interest run booked: ${res.data.lines.length} line${res.data.lines.length === 1 ? '' : 's'}`)
          setNotes('')
          loadRuns()
          loadPosition()
        } finally {
          setBooking(false)
        }
      }
    })
  }, [
    selectedFundId,
    position,
    totals,
    asOf,
    notes,
    functionalCode,
    modalApi,
    currencyText,
    rowBookable,
    rowAmount,
    overrides,
    fxInputs,
    loanOverrides,
    loadRuns,
    loadPosition
  ])

  // ---------------------------------------------------------------------------
  // Runs
  // ---------------------------------------------------------------------------
  const loadRunDetail = useCallback(async (runId: string) => {
    const token = getAccessToken(store.getState().auth)
    const res = await fetchInterestRunDetail(runId, token)
    if (res.error || !res.data) {
      showErrorToast(res.error || 'Failed to fetch the interest run')
      return
    }
    const detail = res.data
    setRunDetails((prev) => ({ ...prev, [runId]: detail }))
  }, [])

  const toggleRun = useCallback(
    (runId: string) => {
      setExpandedRunIds((prev) => {
        if (prev.includes(runId)) return prev.filter((id) => id !== runId)
        return [...prev, runId]
      })
      if (!expandedRunIds.includes(runId)) loadRunDetail(runId)
    },
    [expandedRunIds, loadRunDetail]
  )

  // after a run changed state: its lines, the runs and the position move with it
  const afterRunChange = useCallback(
    (runId: string) => {
      setRunDetails((prev) => {
        const next = { ...prev }
        delete next[runId]
        return next
      })
      if (expandedRunIds.includes(runId)) loadRunDetail(runId)
      loadRuns()
      if (position) loadPosition()
    },
    [expandedRunIds, loadRunDetail, loadRuns, position, loadPosition]
  )

  const handleUnbookRun = useCallback(
    async (run: InterestRun) => {
      const runId = run.asset_interest_runs__id
      setRunActionId(runId)
      try {
        const token = getAccessToken(store.getState().auth)
        const dry = await unbookInterestRun(runId, true, token)
        if (dry.error || !dry.data) {
          showErrorToast(dry.error || 'Failed to unbook the interest run')
          return
        }
        const count = dry.data.transactions_affected ?? 0
        modalApi.confirm({
          title: 'Unbook run?',
          content: `${count} transaction${count === 1 ? '' : 's'} of the ${SIDE_LABEL[run.side].toLowerCase()} run to ${formatDate(
            run.as_of_date
          )} return${count === 1 ? 's' : ''} to Draft and ${count === 1 ? 'its' : 'their'} journals are reversed.`,
          okText: 'Unbook',
          okButtonProps: { danger: true },
          onOk: async () => {
            const res = await unbookInterestRun(runId, false, getAccessToken(store.getState().auth))
            if (res.error || !res.data) {
              showErrorToast(res.error || 'Failed to unbook the interest run')
              return
            }
            toast.success('Interest run unbooked - it is a Draft now')
            afterRunChange(runId)
          }
        })
      } finally {
        setRunActionId(null)
      }
    },
    [modalApi, afterRunChange]
  )

  const handleBookDraftRun = useCallback(
    async (run: InterestRun) => {
      const runId = run.asset_interest_runs__id
      setRunActionId(runId)
      try {
        const res = await bookInterestRun(runId, getAccessToken(store.getState().auth))
        if (res.error || !res.data) {
          showErrorToast(res.error || 'Failed to book the interest run')
          return
        }
        toast.success('Interest run booked')
        afterRunChange(runId)
      } finally {
        setRunActionId(null)
      }
    },
    [afterRunChange]
  )

  const handleDeleteRun = useCallback(
    (run: InterestRun) => {
      const runId = run.asset_interest_runs__id
      modalApi.confirm({
        title: 'Delete run?',
        content: `The ${SIDE_LABEL[run.side].toLowerCase()} run to ${formatDate(
          run.as_of_date
        )} and its Draft transactions are deleted.`,
        okText: 'Delete',
        okButtonProps: { danger: true },
        onOk: async () => {
          const res = await deleteInterestRun(runId, getAccessToken(store.getState().auth))
          if (res.error || !res.data) {
            showErrorToast(res.error || 'Failed to delete the interest run')
            return
          }
          toast.success('Interest run deleted')
          setExpandedRunIds((prev) => prev.filter((id) => id !== runId))
          afterRunChange(runId)
        }
      })
    },
    [modalApi, afterRunChange]
  )

  // ---------------------------------------------------------------------------
  // Columns
  // ---------------------------------------------------------------------------
  const recordLink = useCallback(
    (label: string, onClick: () => void) => (
      <button type='button' className={RECORD_LINK_CLASS} onClick={onClick}>
        {label}
      </button>
    ),
    []
  )

  const receivableStatusCell = useCallback(
    (row: InterestAccrualReceivableRow) => {
      const label = row.prog__status_label ?? row.prog__status
      const tag = <Tag color={RECEIVABLE_STATUS_COLOR[row.prog__status]}>{label}</Tag>
      if (row.prog__status === 'no_terms') {
        return (
          <Space size={4}>
            {tag}
            {row.securities__id
              ? recordLink('Open security', () =>
                  openFormInTab(TABLE_SECURITIES, row.securities__id ?? null, row.securities__name ?? 'Security')
                )
              : null}
          </Space>
        )
      }
      if (row.prog__status === 'exited') {
        return (
          <Tooltip
            title={`Booked interest balance still open: ${currencyText(row.prog__booked_balance ?? 0)} ${
              row.currencies__code ?? ''
            }`}
          >
            {tag}
          </Tooltip>
        )
      }
      if (row.prog__status === 'already_accrued') {
        return <Tooltip title={`Accrued to ${formatDate(row.prog__accrued_to)} - on or after the as-of date`}>{tag}</Tooltip>
      }
      return tag
    },
    [currencyText, openFormInTab, recordLink]
  )

  const receivableColumns: ColumnsType<InterestAccrualReceivableRow> = useMemo(
    () => [
      {
        title: 'Company',
        key: 'company',
        fixed: 'left',
        render: (_: unknown, row) =>
          row.accounts__id
            ? recordLink(row.accounts__name ?? 'Company', () =>
                openFormInTab(TABLE_ACCOUNTS, row.accounts__id ?? null, row.accounts__name ?? 'Company')
              )
            : row.accounts__name ?? '—'
      },
      { title: 'Security', dataIndex: 'securities__name', key: 'security', render: (v: string | null) => v ?? '—' },
      {
        title: 'Tranche',
        key: 'tranche',
        render: (_: unknown, row) =>
          recordLink(row.tranches__name ?? 'Tranche', () =>
            openFormInTab(TABLE_TRANCHES, row.tranches__id, row.tranches__name ?? 'Tranche')
          )
      },
      { title: 'Ccy', dataIndex: 'currencies__code', key: 'ccy', render: (v: string | null) => v ?? '—' },
      {
        title: 'Principal',
        dataIndex: 'prog__principal',
        key: 'principal',
        align: 'right',
        render: (v: number | null) => <NumberText value={v} />
      },
      { title: 'Rate', dataIndex: 'prog__rate', key: 'rate', align: 'right', render: (v: number | null) => formatRate(v) },
      { title: 'Terms', dataIndex: 'prog__terms_label', key: 'terms', render: (v: string | null) => v ?? '—' },
      {
        title: 'Accrued to date',
        dataIndex: 'prog__accrued_to_date',
        key: 'accrued_to_date',
        align: 'right',
        render: (v: number | null) => <NumberText value={v} />
      },
      {
        title: 'Booked before',
        dataIndex: 'prog__booked_before',
        key: 'booked_before',
        align: 'right',
        render: (v: number | null) => <NumberText value={v} />
      },
      {
        title: 'This booking',
        key: 'this_booking',
        children: [
          {
            title: 'Calculated',
            dataIndex: 'prog__this_booking',
            key: 'calculated',
            align: 'right',
            render: (v: number | null, row) =>
              overrides[row.tranches__id] != null ? (
                <Text delete type='secondary'>
                  <NumberText value={v} />
                </Text>
              ) : (
                <NumberText value={v} />
              )
          },
          {
            title: 'Override',
            key: 'override',
            width: 200,
            render: (_: unknown, row) => {
              if (!RECEIVABLE_BOOKABLE_STATUSES.has(row.prog__status)) return null
              const value = overrides[row.tranches__id] ?? null
              return (
                <Space size={4}>
                  <InputNumber
                    {...currencyFmt}
                    size='small'
                    style={{ width: 120 }}
                    aria-label={`Override ${row.tranches__name ?? ''}`}
                    status={value != null ? 'warning' : undefined}
                    value={value}
                    onChange={(next) =>
                      setOverrides((prev) => ({
                        ...prev,
                        [row.tranches__id]: typeof next === 'number' ? next : null
                      }))
                    }
                  />
                  {value != null ? <Tag color='gold'>Override</Tag> : null}
                </Space>
              )
            }
          }
        ]
      },
      {
        title: 'FX rate',
        key: 'fx_rate',
        align: 'right',
        render: (_: unknown, row) => {
          if (row.prog__fx_rate != null) return <NumberText value={row.prog__fx_rate} formatKey='fx_rate' />
          if (!RECEIVABLE_BOOKABLE_STATUSES.has(row.prog__status)) return '—'
          const value = fxInputs[row.tranches__id] ?? null
          return (
            <Tooltip title={`The FX feed has no ${row.currencies__code ?? ''} rate for ${formatDate(asOf)} - enter one`}>
              <InputNumber
                {...fxFmt}
                size='small'
                style={{ width: 100 }}
                min={0}
                aria-label={`FX rate ${row.tranches__name ?? ''}`}
                status={value == null ? 'warning' : undefined}
                value={value}
                onChange={(next) =>
                  setFxInputs((prev) => ({ ...prev, [row.tranches__id]: typeof next === 'number' ? next : null }))
                }
              />
            </Tooltip>
          )
        }
      },
      {
        title: `Functional${functionalCode ? ` (${functionalCode})` : ''}`,
        key: 'functional',
        align: 'right',
        render: (_: unknown, row) => <NumberText value={rowFunctional(row)} />
      },
      { title: 'Status', key: 'status', render: (_: unknown, row) => receivableStatusCell(row) }
    ],
    [
      recordLink,
      openFormInTab,
      overrides,
      fxInputs,
      currencyFmt,
      fxFmt,
      asOf,
      functionalCode,
      rowFunctional,
      receivableStatusCell
    ]
  )

  const setLoanOverride = useCallback((loanId: string, component: keyof LoanOverride, value: number | null) => {
    setLoanOverrides((prev) => ({
      ...prev,
      [loanId]: { interest: prev[loanId]?.interest ?? null, charge: prev[loanId]?.charge ?? null, [component]: value }
    }))
  }, [])

  const payableColumns: ColumnsType<InterestAccrualPayableRow> = useMemo(() => {
    const overrideInput = (row: InterestAccrualPayableRow, component: keyof LoanOverride) => {
      if (row.prog__status === 'already_accrued') return null
      const value = loanOverrides[row.fund_loans__id]?.[component] ?? null
      return (
        <Space size={4}>
          <InputNumber
            {...currencyFmt}
            size='small'
            style={{ width: 120 }}
            aria-label={`Override ${component} ${row.fund_loans__reference ?? ''}`}
            status={value != null ? 'warning' : undefined}
            value={value}
            onChange={(next) => setLoanOverride(row.fund_loans__id, component, typeof next === 'number' ? next : null)}
          />
          {value != null ? <Tag color='gold'>Override</Tag> : null}
        </Space>
      )
    }
    const calculated = (v: number | null | undefined, overridden: boolean) =>
      overridden ? (
        <Text delete type='secondary'>
          <NumberText value={v} />
        </Text>
      ) : (
        <NumberText value={v} />
      )
    return [
      {
        title: 'Loan',
        key: 'loan',
        fixed: 'left',
        render: (_: unknown, row) => (
          <span>
            {row.fund_loans__reference ?? '—'}
            {row.fund_loans__name ? <Text type='secondary'> · {row.fund_loans__name}</Text> : null}
          </span>
        )
      },
      { title: 'Ccy', dataIndex: 'currencies__code', key: 'ccy', render: (v: string | null) => v ?? '—' },
      {
        title: 'Principal',
        dataIndex: 'prog__principal',
        key: 'principal',
        align: 'right',
        render: (v: number | null) => <NumberText value={v} />
      },
      { title: 'Rate', dataIndex: 'prog__rate', key: 'rate', align: 'right', render: (v: number | null) => formatRate(v) },
      {
        title: 'Charge rate',
        dataIndex: 'prog__charge_rate',
        key: 'charge_rate',
        align: 'right',
        render: (v: number | null) => formatRate(v)
      },
      { title: 'Terms', dataIndex: 'prog__terms_label', key: 'terms', render: (v: string | null) => v ?? '—' },
      {
        title: 'Accrued to date',
        key: 'accrued_to_date',
        children: [
          {
            title: 'Interest',
            dataIndex: 'prog__interest_accrued_to_date',
            key: 'interest_accrued',
            align: 'right',
            render: (v: number | null) => <NumberText value={v} />
          },
          {
            title: 'Charge',
            dataIndex: 'prog__charge_accrued_to_date',
            key: 'charge_accrued',
            align: 'right',
            render: (v: number | null) => <NumberText value={v} />
          }
        ]
      },
      {
        title: 'Booked before',
        key: 'booked_before',
        children: [
          {
            title: 'Interest',
            dataIndex: 'prog__interest_booked_before',
            key: 'interest_booked',
            align: 'right',
            render: (v: number | null) => <NumberText value={v} />
          },
          {
            title: 'Charge',
            dataIndex: 'prog__charge_booked_before',
            key: 'charge_booked',
            align: 'right',
            render: (v: number | null) => <NumberText value={v} />
          }
        ]
      },
      {
        title: 'This booking',
        key: 'this_booking',
        children: [
          {
            title: 'Interest',
            dataIndex: 'prog__this_interest',
            key: 'this_interest',
            align: 'right',
            render: (v: number | null, row) => calculated(v, loanOverrides[row.fund_loans__id]?.interest != null)
          },
          {
            title: 'Charge',
            dataIndex: 'prog__this_charge',
            key: 'this_charge',
            align: 'right',
            render: (v: number | null, row) => calculated(v, loanOverrides[row.fund_loans__id]?.charge != null)
          },
          {
            title: 'Total',
            key: 'this_total',
            align: 'right',
            render: (_: unknown, row) => <NumberText value={round2(loanInterest(row) + loanCharge(row))} />
          }
        ]
      },
      {
        title: 'Override',
        key: 'override',
        children: [
          { title: 'Interest', key: 'override_interest', width: 200, render: (_: unknown, row) => overrideInput(row, 'interest') },
          { title: 'Charge', key: 'override_charge', width: 200, render: (_: unknown, row) => overrideInput(row, 'charge') }
        ]
      },
      {
        title: 'Status',
        key: 'status',
        render: (_: unknown, row) => {
          const tag = <Tag color={PAYABLE_STATUS_COLOR[row.prog__status]}>{row.prog__status_label ?? row.prog__status}</Tag>
          return row.prog__status === 'already_accrued' ? (
            <Tooltip title={`Accrued to ${formatDate(row.prog__accrued_to)} - on or after the as-of date`}>{tag}</Tooltip>
          ) : (
            tag
          )
        }
      }
    ]
  }, [loanOverrides, currencyFmt, setLoanOverride, loanInterest, loanCharge])

  const receivableScheduleColumns: ColumnsType<ReceivableScheduleRow> = useMemo(
    () => [
      { title: 'Period start', dataIndex: 'period_start', key: 'start', render: (v: string | null) => formatDate(v) },
      { title: 'Period end', dataIndex: 'period_end', key: 'end', render: (v: string | null) => formatDate(v) },
      { title: 'Days', dataIndex: 'days', key: 'days', align: 'right' },
      { title: 'Principal', dataIndex: 'principal', key: 'principal', align: 'right', render: (v: number | null) => <NumberText value={v} /> },
      {
        title: 'Capitalised interest',
        dataIndex: 'capitalised_interest',
        key: 'capitalised',
        align: 'right',
        render: (v: number | null) => <NumberText value={v} />
      },
      { title: 'Rate', dataIndex: 'interest_rate', key: 'rate', align: 'right', render: (v: number | null) => formatRate(v) },
      { title: 'Day count', dataIndex: 'day_count_label', key: 'day_count', render: (v: string | null) => v ?? '—' },
      { title: 'Interest', dataIndex: 'interest', key: 'interest', align: 'right', render: (v: number | null) => <NumberText value={v} /> },
      { title: 'Capitalises', dataIndex: 'capitalises', key: 'capitalises', render: (v: boolean | null) => (v ? 'Yes' : '—') }
    ],
    []
  )

  const payableScheduleColumns: ColumnsType<PayableScheduleRow> = useMemo(
    () => [
      { title: 'Period start', dataIndex: 'period_start', key: 'start', render: (v: string | null) => formatDate(v) },
      { title: 'Period end', dataIndex: 'period_end', key: 'end', render: (v: string | null) => formatDate(v) },
      { title: 'Days', dataIndex: 'days', key: 'days', align: 'right' },
      { title: 'Principal', dataIndex: 'principal', key: 'principal', align: 'right', render: (v: number | null) => <NumberText value={v} /> },
      {
        title: 'Capitalised interest',
        dataIndex: 'capitalised_interest',
        key: 'capitalised',
        align: 'right',
        render: (v: number | null) => <NumberText value={v} />
      },
      { title: 'Rate', dataIndex: 'interest_rate', key: 'rate', align: 'right', render: (v: number | null) => formatRate(v) },
      { title: 'Charge rate', dataIndex: 'charge_rate', key: 'charge_rate', align: 'right', render: (v: number | null) => formatRate(v) },
      { title: 'Day count', dataIndex: 'day_count_label', key: 'day_count', render: (v: string | null) => v ?? '—' },
      { title: 'Interest', dataIndex: 'interest', key: 'interest', align: 'right', render: (v: number | null) => <NumberText value={v} /> },
      { title: 'Charge', dataIndex: 'charge', key: 'charge', align: 'right', render: (v: number | null) => <NumberText value={v} /> },
      { title: 'Capitalises', dataIndex: 'capitalises', key: 'capitalises', render: (v: boolean | null) => (v ? 'Yes' : '—') }
    ],
    []
  )

  const previewColumns: ColumnsType<PreviewLine> = useMemo(
    () => [
      { title: 'Item', dataIndex: 'acc_items__label', key: 'item', render: (v: string | null) => v ?? '—' },
      {
        title: 'Account',
        key: 'account',
        render: (_: unknown, line) =>
          line.acc_chart_accounts__number ? (
            `${line.acc_chart_accounts__number} · ${line.acc_chart_accounts__name ?? ''}`
          ) : (
            <Tag color='red'>No account</Tag>
          )
      },
      {
        title: 'Debit',
        dataIndex: 'prog__debit',
        key: 'debit',
        align: 'right',
        render: (v: number | null) => (isNonZero(v) ? <NumberText value={v} /> : '')
      },
      {
        title: 'Credit',
        dataIndex: 'prog__credit',
        key: 'credit',
        align: 'right',
        render: (v: number | null) => (isNonZero(v) ? <NumberText value={v} /> : '')
      }
    ],
    []
  )

  const runColumns: ColumnsType<InterestRun> = useMemo(
    () => [
      { title: 'As of', dataIndex: 'as_of_date', key: 'as_of', render: (v: string | null) => formatDate(v) },
      { title: 'Side', dataIndex: 'side', key: 'side', render: (v: InterestSide) => SIDE_LABEL[v] },
      {
        title: 'Status',
        dataIndex: 'status_label',
        key: 'status',
        render: (v: string | null) => (v ? <Tag color={RUN_STATUS_COLOR[v] ?? 'default'}>{v}</Tag> : '—')
      },
      { title: 'Lines', dataIndex: 'line_count', key: 'lines', align: 'right', render: (v: number | null) => v ?? 0 },
      {
        title: 'Total (functional)',
        dataIndex: 'total_amount_functional',
        key: 'total',
        align: 'right',
        render: (v: number | null) => <NumberText value={v} />
      },
      { title: 'Notes', dataIndex: 'notes', key: 'notes', render: (v: string | null) => v || '—' },
      { title: 'Created', dataIndex: 'date_created', key: 'created', render: (v: string | null) => formatDate(v) },
      {
        title: '',
        key: 'actions',
        align: 'right',
        render: (_: unknown, run) => {
          const runId = run.asset_interest_runs__id
          const busy = runActionId === runId
          return (
            <Space size={4}>
              <Button size='small' onClick={() => toggleRun(runId)}>
                {expandedRunIds.includes(runId) ? 'Close' : 'Open'}
              </Button>
              {run.status_label === 'Booked' ? (
                <DisabledReason reason={unbookRight.reason}>
                  <Button
                    danger
                    size='small'
                    loading={busy}
                    disabled={!unbookRight.allowed}
                    onClick={() => handleUnbookRun(run)}
                  >
                    Unbook
                  </Button>
                </DisabledReason>
              ) : null}
              {run.status_label === 'Draft' ? (
                <>
                  <DisabledReason reason={bookRight.reason}>
                    <Button
                      type='primary'
                      size='small'
                      loading={busy}
                      disabled={!bookRight.allowed}
                      onClick={() => handleBookDraftRun(run)}
                    >
                      Book
                    </Button>
                  </DisabledReason>
                  <DisabledReason reason={deleteRight.reason}>
                    <Button
                      danger
                      size='small'
                      disabled={busy || !deleteRight.allowed}
                      onClick={() => handleDeleteRun(run)}
                    >
                      Delete
                    </Button>
                  </DisabledReason>
                </>
              ) : null}
            </Space>
          )
        }
      }
    ],
    [runActionId, expandedRunIds, toggleRun, handleUnbookRun, handleBookDraftRun, handleDeleteRun]
  )

  const renderRunLines = (run: InterestRun) => {
    const detail = runDetails[run.asset_interest_runs__id]
    if (!detail) return <Text type='secondary'>Loading the run's lines…</Text>
    if (detail.side === 'receivable') {
      const columns: ColumnsType<InterestRunReceivableLine> = [
        {
          title: 'Transaction',
          key: 'transaction',
          render: (_: unknown, line) =>
            recordLink(line.asset_transactions__name ?? 'Asset transaction', () =>
              openFormInTab(
                TABLE_ASSET_TRANSACTIONS,
                line.asset_transactions__id,
                line.asset_transactions__name ?? 'Asset Transaction'
              )
            )
        },
        {
          title: 'Tranche',
          key: 'tranche',
          render: (_: unknown, line) =>
            line.tranches__id
              ? recordLink(line.tranches__name ?? 'Tranche', () =>
                  openFormInTab(TABLE_TRANCHES, line.tranches__id ?? null, line.tranches__name ?? 'Tranche')
                )
              : line.tranches__name ?? '—'
        },
        { title: 'Company', dataIndex: 'accounts__name', key: 'company', render: (v: string | null) => v ?? '—' },
        { title: 'Ccy', dataIndex: 'currencies__code', key: 'ccy', render: (v: string | null) => v ?? '—' },
        { title: 'Amount', dataIndex: 'amount', key: 'amount', align: 'right', render: (v: number | null) => <NumberText value={v} /> },
        {
          title: 'Functional',
          dataIndex: 'amount_functional',
          key: 'functional',
          align: 'right',
          render: (v: number | null) => <NumberText value={v} />
        },
        {
          title: 'Status',
          dataIndex: 'status_label',
          key: 'status',
          render: (v: string | null) => (v ? <Tag color={RUN_STATUS_COLOR[v] ?? 'default'}>{v}</Tag> : '—')
        }
      ]
      return (
        <Table
          size='small'
          pagination={false}
          rowKey='asset_transactions__id'
          dataSource={detail.lines}
          columns={columns}
          scroll={{ x: 'max-content' }}
        />
      )
    }
    const columns: ColumnsType<InterestRunPayableLine> = [
      { title: 'Loan', dataIndex: 'fund_loans__reference', key: 'loan', render: (v: string | null) => v ?? '—' },
      {
        title: 'Component',
        dataIndex: 'component',
        key: 'component',
        render: (v: string | null, line) => line.fund_loan_fees__description ?? v ?? '—'
      },
      {
        title: 'Calculated',
        dataIndex: 'calculated_amount',
        key: 'calculated',
        align: 'right',
        render: (v: number | null) => <NumberText value={v} />
      },
      {
        title: 'Override',
        dataIndex: 'override_amount',
        key: 'override',
        align: 'right',
        render: (v: number | null) => <NumberText value={v} blank='' />
      },
      {
        title: 'Booked',
        dataIndex: 'prog__effective_amount',
        key: 'effective',
        align: 'right',
        render: (v: number | null) => <NumberText value={v} />
      }
    ]
    return (
      <div className='flex flex-col gap-2'>
        {detail.run.fund_transaction_id ? (
          <div>
            <Text type='secondary'>Fund transaction: </Text>
            {recordLink('Loan Interest (Accrual)', () =>
              openFormInTab(TABLE_FUND_TRANSACTIONS, detail.run.fund_transaction_id ?? null, 'Loan Interest (Accrual)')
            )}
          </div>
        ) : null}
        <Table
          size='small'
          pagination={false}
          rowKey='id'
          dataSource={detail.lines}
          columns={columns}
          scroll={{ x: 'max-content' }}
        />
      </div>
    )
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const periodOptions = useMemo(() => quarterEnds(dayjs(asOf).year()), [asOf])
  const lastRun = useMemo(() => {
    if (fund?.last_run) return fund.last_run
    return runs.find((r) => r.side === side) ?? null
  }, [fund, runs, side])

  // A receivable run creates asset transactions, then books them; a payable
  // run creates a fund transaction and books it (docs/USER_RIGHTS_DESIGN.md 6.2).
  const bookDisabledReason =
    side === 'receivable' && !createRight.allowed
      ? createRight.reason
      : !bookRight.allowed
        ? bookRight.reason
        : !selectedFundId
          ? 'Select a fund first.'
          : !position
            ? 'Compute first.'
            : totals.bookableCount === 0
              ? 'Nothing to book: no line has an amount to accrue.'
              : null

  const actionBar = (
    <div className='flex items-center justify-between mb-2'>
      <Radio.Group
        optionType='button'
        buttonStyle='solid'
        size='small'
        value={side}
        onChange={(e) => {
          setSide(e.target.value === 'payable' ? 'payable' : 'receivable')
          clearPosition()
        }}
        options={[
          { value: 'receivable', label: 'Receivable' },
          { value: 'payable', label: 'Payable' }
        ]}
      />
      <Space>
        <Button
          icon={<CalculatorOutlined />}
          size='small'
          onClick={() => loadPosition()}
          loading={loading}
          disabled={!selectedFundId}
        >
          Compute
        </Button>
        <Tooltip title={bookDisabledReason}>
          <span>
            <Button
              type='primary'
              size='small'
              loading={booking}
              disabled={loading || Boolean(bookDisabledReason)}
              onClick={handleBookRun}
            >
              Book run
            </Button>
          </span>
        </Tooltip>
      </Space>
    </div>
  )

  if (embedded) {
    return (
      <div>
        {modalContextHolder}
        {actionBar}
        <div className='flex flex-col gap-3'>{renderSections()}</div>
      </div>
    )
  }

  return (
    <div className='h-full overflow-y-auto bg-gray-50 p-3'>
      {modalContextHolder}
      {actionBar}
      <div className='flex flex-col gap-3'>{renderSections()}</div>
    </div>
  )

  function renderGrid() {
    if (!position) {
      return (
        <Text type='secondary'>
          Not computed. Compute reads the terms and the booked position; nothing is written.
        </Text>
      )
    }
    if (position.side === 'receivable') {
      return (
        <Table<InterestAccrualReceivableRow>
          size='small'
          pagination={false}
          rowKey='tranches__id'
          dataSource={position.data.rows}
          columns={receivableColumns}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'No tranche of this fund holds a security with interest terms.' }}
          expandable={{
            columnWidth: 48,
            fixed: 'left',
            rowExpandable: (row) => (row.schedule?.length ?? 0) > 0,
            expandedRowRender: (row) => (
              <div className='flex flex-col gap-1'>
                <Text type='secondary'>The periods the engine accrued to {formatDate(asOf)}.</Text>
                <Table<ReceivableScheduleRow>
                  size='small'
                  pagination={false}
                  rowKey={(r) => `${r.period_start ?? ''}-${r.period_end ?? ''}`}
                  dataSource={row.schedule ?? []}
                  columns={receivableScheduleColumns}
                />
              </div>
            )
          }}
          summary={() => (
            <Table.Summary fixed>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} />
                <Table.Summary.Cell index={1} colSpan={12}>
                  <Text strong>
                    Total · {totals.bookableCount} of {totals.lineCount} bookable
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={13} align='right'>
                  <NumberText value={totals.functional} wrap={(v) => <Text strong>{v}</Text>} />
                </Table.Summary.Cell>
                <Table.Summary.Cell index={14} />
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      )
    }
    const sum = (fn: (r: InterestAccrualPayableRow) => number | null | undefined) =>
      payableRows.reduce((acc, r) => acc + (fn(r) ?? 0), 0)
    const strong = (v: number) => <NumberText value={round2(v)} wrap={(t) => <Text strong>{t}</Text>} />
    return (
      <Table<InterestAccrualPayableRow>
        size='small'
        pagination={false}
        rowKey='fund_loans__id'
        dataSource={position.data.rows}
        columns={payableColumns}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: `This fund has no loans issued by ${formatDate(asOf)}.` }}
        expandable={{
          columnWidth: 48,
          fixed: 'left',
          rowExpandable: (row) => (row.schedule?.length ?? 0) > 0,
          expandedRowRender: (row) => (
            <div className='flex flex-col gap-1'>
              <Text type='secondary'>The periods the loan engine accrued to {formatDate(asOf)}.</Text>
              <Table<PayableScheduleRow>
                size='small'
                pagination={false}
                rowKey={(r) => `${r.period_start ?? ''}-${r.period_end ?? ''}`}
                dataSource={row.schedule ?? []}
                columns={payableScheduleColumns}
              />
            </div>
          )
        }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} />
              <Table.Summary.Cell index={1} colSpan={2}>
                <Text strong>
                  Total · {totals.bookableCount} of {totals.lineCount} bookable
                </Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={3} align='right'>
                {strong(sum((r) => r.prog__principal))}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} colSpan={3} />
              <Table.Summary.Cell index={7} align='right'>
                {strong(sum((r) => r.prog__interest_accrued_to_date))}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={8} align='right'>
                {strong(sum((r) => r.prog__charge_accrued_to_date))}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={9} align='right'>
                {strong(sum((r) => r.prog__interest_booked_before))}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={10} align='right'>
                {strong(sum((r) => r.prog__charge_booked_before))}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={11} align='right'>
                {strong(sum((r) => r.prog__this_interest))}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={12} align='right'>
                {strong(sum((r) => r.prog__this_charge))}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={13} align='right'>
                {strong(totals.functional)}
              </Table.Summary.Cell>
              <Table.Summary.Cell index={14} colSpan={3} />
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    )
  }

  function renderSections() {
    const warnings = position?.data.warnings ?? []
    const preview = previewLines
    const previewDebit = preview.reduce((acc, l) => acc + l.prog__debit, 0)
    const previewCredit = preview.reduce((acc, l) => acc + l.prog__credit, 0)
    return (
      <>
        <FormSection title='Parameters' titleColor='orange'>
          <Row gutter={[12, 12]}>
            {!embedded && (
              <Col xs={24} md={6}>
                <div className='mb-1 text-xs text-gray-500'>Fund</div>
                <FundSelect
                  style={{ width: '100%' }}
                  placeholder='Select fund'
                  value={selectedFundId}
                  onChange={(value: string | null) => {
                    setSelectedFundId(value ?? null)
                    clearPosition()
                  }}
                />
              </Col>
            )}
            <Col xs={12} md={4}>
              <div className='mb-1 text-xs text-gray-500'>As of</div>
              <DatePicker
                style={{ width: '100%' }}
                format={DATE_FORMAT}
                value={dayjs(asOf)}
                allowClear={false}
                onChange={(d) => {
                  if (!d) return
                  setAsOf(d.format(ISO_FORMAT))
                  clearPosition()
                }}
              />
            </Col>
            <Col xs={12} md={4}>
              <div className='mb-1 text-xs text-gray-500'>Period</div>
              <Select
                style={{ width: '100%' }}
                placeholder='Quarter end'
                options={periodOptions}
                value={periodOptions.find((p) => p.value === asOf)?.value ?? null}
                onChange={(v: string) => {
                  setAsOf(v)
                  clearPosition()
                }}
              />
            </Col>
            <Col xs={24} md={4}>
              <div className='mb-1 text-xs text-gray-500'>FX for the functional twin</div>
              <div>Accrual-date rate (ECB feed)</div>
            </Col>
            <Col xs={24} md={6}>
              <div className='mb-1 text-xs text-gray-500'>Notes</div>
              <Input
                placeholder='Optional - stored on the run and its transactions'
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Col>
            <Col xs={24}>
              <Text type='secondary'>
                Last run:{' '}
                {lastRun
                  ? `${formatDate(lastRun.as_of_date)} · ${SIDE_LABEL[side]} · ${lastRun.status_label ?? '—'} · ${
                      lastRun.line_count ?? 0
                    } line${lastRun.line_count === 1 ? '' : 's'}`
                  : 'none'}
              </Text>
            </Col>
          </Row>
        </FormSection>

        {warnings.length > 0 ? (
          <Alert
            type='warning'
            showIcon
            message={warnings.length === 1 ? warnings[0] : 'Check before booking'}
            description={
              warnings.length > 1 ? (
                <ul className='m-0 pl-4'>
                  {warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              ) : undefined
            }
          />
        ) : null}

        <FormSection
          title={`Calculated accrual${fund?.funds__name ? ` · ${fund.funds__name}` : ''} · to ${formatDate(asOf)}`}
          titleColor='orange'
          loading={loading}
        >
          {!selectedFundId ? <Text type='secondary'>Select a fund.</Text> : renderGrid()}
          {position ? (
            <div className='mt-1'>
              <Text type='secondary' style={{ fontSize: 12 }}>
                This booking = interest accrued to date minus interest booked before, so a run trues up whatever
                was booked earlier. An override books the amount entered instead (in the{' '}
                {position.side === 'receivable' ? 'security' : 'loan'} currency).
              </Text>
            </div>
          ) : null}
        </FormSection>

        {position ? (
          <FormSection
            title='Journal preview · this run'
            titleColor='orange'
            titleSuffix={<Tag color='blue'>Proposed</Tag>}
          >
            <Table<PreviewLine>
              size='small'
              pagination={false}
              rowKey='preview_key'
              dataSource={preview}
              columns={previewColumns}
              locale={{ emptyText: 'Nothing to book.' }}
              summary={() =>
                preview.length > 0 ? (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={2}>
                      <Text strong>Total ({functionalCode || 'functional'})</Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align='right'>
                      <NumberText value={round2(previewDebit)} wrap={(v) => <Text strong>{v}</Text>} />
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align='right'>
                      <NumberText value={round2(previewCredit)} wrap={(v) => <Text strong>{v}</Text>} />
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                ) : null
              }
            />
          </FormSection>
        ) : null}

        {selectedFundId ? (
          <FormSection
            title='Runs'
            titleColor='orange'
            loading={runsLoading}
            headerActions={
              <ExportRowsButton
                name={`${getFundNameById(selectedFundId) || 'Fund'} Interest accrual runs`}
                rows={runs}
                columns={[
                  { title: 'As of', value: (r) => r.as_of_date, kind: 'date' },
                  { title: 'Side', value: (r) => SIDE_LABEL[r.side] },
                  { title: 'Status', value: (r) => r.status_label },
                  { title: 'Lines', value: (r) => r.line_count ?? 0 },
                  { title: 'Total (functional)', value: (r) => r.total_amount_functional },
                  { title: 'Notes', value: (r) => r.notes },
                  { title: 'Created', value: (r) => r.date_created, kind: 'date' }
                ]}
              />
            }
          >
            <Table<InterestRun>
              size='small'
              pagination={false}
              rowKey='asset_interest_runs__id'
              dataSource={runs}
              columns={runColumns}
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: 'No interest runs for this fund yet.' }}
              expandable={{
                columnWidth: 48,
                expandedRowKeys: expandedRunIds,
                onExpand: (_expanded, run) => toggleRun(run.asset_interest_runs__id),
                expandedRowRender: (run) => renderRunLines(run)
              }}
            />
          </FormSection>
        ) : null}
      </>
    )
  }
}
